<template>
  <div class="styled-basket">
    <template v-if="!refresh || !loading">
      <base-basket :show-carriage="showCarriage"
                   #default="{
                  orderLines,
                  voucherCodes,
                  totals,
                  refreshData,
                  expectingNewLine,
                  initialLoading,
                  messages,
                  clearMessages,
                }"
                   @success="refreshData()">
        <template v-if="!initialLoading">
          <b-alert
            :show="messages && messages.length > 0"
            dismissible
            @dismissed="clearMessages">
            <h2 class="alert-heading">
              Note:
            </h2>
            <ul class="alert-list">
              <li
                v-for="(message, index) in messages"
                :key="index"
                class="alert-item">
                {{ message }}
              </li>
            </ul>
          </b-alert>
          <template v-if="orderLines && orderLines.length > 0">
            <simple-basket-order-lines-list :no-delete="readonly">
              <simple-basket-order-line v-for="( orderLine, index ) in orderLines"
                                        :key="orderLine.id"
                                        v-model="orderLines[index]"
                                        :lock-quantity="readonly"
                                        :no-delete="readonly"
                                        :input-class="inputClass"
                                        :currency="currency"
                                        :locale="locale"
                                        btn-class="btn btn-danger"
                                        @update="refreshData()">
                <template #default>
                  <div>
                    <span class="product-display-name">
                      {{ orderLine.productDisplayName }}
                    </span>

                    <span v-if="orderLine.hasWebPage"
                          class="product-display-icon">
                      <font-awesome-icon icon="external-link"></font-awesome-icon>
                    </span>
                  </div>

                  <div class="styled-basket__pricing">
                    {{ getPricingDisplayString( orderLine ) }}
                  </div>
                </template>
                <template #delete-label>
                  <font-awesome-icon :icon="['far', 'trash-alt']"></font-awesome-icon>
                </template>
              </simple-basket-order-line>

              <tr v-if="expectingNewLine"
                  class="simple-basket__loading">
                <td colspan="100%">
                  <loading-spinner></loading-spinner>
                </td>
              </tr>
            </simple-basket-order-lines-list>

            <div class="simple-basket__summary">
              <div class="simple-basket__pricing">
                <div v-if="voucherError"
                     class="alert alert-danger simple-basket__error">
                  {{ voucherError }}
                </div>

                <order-totals :value="totals"
                              :currency="currency"
                              :locale="locale"></order-totals>

                <apply-voucher v-if="enableVouchers"
                               :applied-voucher-codes="voucherCodes"
                               :input-class="inputClass"
                               :btn-class="btnClass"
                               @update="refreshData()"
                               @response="onApplyVoucherResponse"></apply-voucher>
              </div>

              <div v-if="enableClear"
                   class="simple-basket__clear">
                <delete-all-order-lines class="btn btn-danger"
                                        @update="refreshData()"></delete-all-order-lines>
              </div>

              <slot name="submit"></slot>
            </div>
          </template>

          <template v-else>
            <div class="simple-basket__empty">
              <slot name="empty">
                Your basket is currently empty.
              </slot>
            </div>
          </template>
        </template>
        <template v-else>
          <loading-spinner size="2x"
                           class="loading-container"
                           label="Loading..."></loading-spinner>
        </template>
      </base-basket>
    </template>
    <template v-else>
      <slot name="loading">
        <loading-spinner size="2x"
                         class="loading-container"
                         label="Loading..."></loading-spinner>
      </slot>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import Ecommerce from "spiritweb.packages.ecommerce";
import { getBasketProducts } from "~/Scripts/services/basketStepService";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";

Vue.use( Ecommerce );

export default {
  name: "StyledBasket",
  components: {
    LoadingSpinner,
  },
  props: {
    enableVouchers: {
      type: Boolean,
      default: false,
    },
    enableClear: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: String,
      default: "form-control",
    },
    btnClass: {
      type: String,
      default: null,
    },
    checkoutUrl: {
      type: String,
      default: null,
    },
    webAccountId: {
      type: String,
      default: null,
    },
    bookingValidations: {
      type: Object,
      default: () => null,
    },
    addressValidations: {
      type: Object,
      default: () => null,
    },
    searchTypeId: {
      type: String,
      default: null,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
    loggedIn: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "GBP",
    },
    locale: {
      type: String,
      default: "en-GB",
    },
    showCarriage: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      selectedOrderLine: {},
      selectedPricingSchedule: null,
      showDeliveryAddressModal: null,
      products: [],
      internalLoggedIn: false,
      voucherError: null,
      loading: false,
    };
  },
  computed: {
    selectedOrderLines () {
      let result = null;

      if ( this.selectedOrderLine?.id ) {
        result = [this.selectedOrderLine.id];
      }

      return result;
    },
  },
  watch: {
    refresh ( val ) {
      if ( val ) {
        this.refreshData();
      }
    },
  },
  methods: {
    refreshData () {
      getBasketProducts()
        .then( ( response ) => {
          this.products = response.data;
        } )
        .finally( () => {
          this.$emit( "refreshed" );
        } );
    },
    refreshAddresses () {
      this.$emit( "refresh-addresses" );
    },
    getAddressValidity ( orderLineId ) {
      let result = null;

      if ( this.addressValidations?.orderLines ) {
        const match = this.addressValidations.orderLines.find( x => x.orderLineId === orderLineId );

        if ( match ) {
          result = match;
        }
      }

      return result;
    },
    requiresAddress ( orderLineId ) {
      let result = false;

      if ( this.addressValidations ) {
        const validity = this.getAddressValidity( orderLineId );

        if ( validity && !validity.valid ) {
          result = true;
        }
      }

      return result;
    },
    getPricingDisplayString ( orderLine ) {
      let result = "";

      if ( orderLine ) {
        result = orderLine.pricingScheduleDescription;
      }

      if ( orderLine.productLicenceTypeId > 0 || orderLine.productFormatId > 0 ) {
        let suffix = "";

        if ( orderLine.productFormatId > 0 ) {
          suffix += orderLine.productFormat;
        }

        if ( orderLine.productLicenceTypeId > 0 ) {
          if ( suffix.length ) {
            suffix += ", ";
          }

          suffix += orderLine.productLicenceType;
        }

        result += ` - (${suffix})`;
      }

      return result;
    },
    onEditAddress ( orderLine ) {
      this.selectedOrderLine = orderLine;
      this.showDeliveryAddressModal = true;
    },
    onApplyVoucherResponse ( response ) {
      this.voucherError = null;

      if ( response ) {
        if ( !response.valid ) {
          this.voucherError = response.userMessage;
        }
      }
    },
    getMatchingProduct ( productId ) {
      let result = null;

      if ( this.products ) {
        const match = this.products.find( x => x.productId === productId );

        if ( match ) {
          result = match;
        }
      }

      return result;
    },
    showAddressButton ( productId ) {
      let result = false;

      const product = this.getMatchingProduct( productId );
      if ( product ) {
        result = product.RequiresDeliveryAddressOnSopLine;
      }

      return result;
    },
    onCloseDeliveryAddressModal () {
      this.selectedOrderLine = null;
      this.showDeliveryAddressModal = false;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.styled-basket {
  border-radius: 5px;
  box-shadow: 0 0 5px 0 rgba( 0, 0, 0, .2 );
  padding: 10px 15px;

  &__pricing {
    color: #777;
    font-size: 14px;
  }

  /deep/ .order-lines-list__table {
    table-layout: fixed;
  }

  /deep/.order-lines-list__content {
    margin: 0;
  }

  /deep/ th {
    font-size: 16px;
    font-weight: 700;
  }

  /deep/ tr {
    border-bottom: 1px solid lightgray;
  }

  /deep/ tbody tr:last-child {
    border-bottom: 0;
  }

  /deep/ .order-lines-list {
    .order-line__name {
      width: 100%;
      font-weight: 600;
    }

    .order-line__quantity {
      width: 100px;

      .form-control {
        padding: 0.375rem 0.75rem;
      }
    }

    .order-line__price {
      border-radius: 10px;
      padding: 5px;
      text-align: center;
      font-weight: 600;
      width: 100px;

      @media ( min-width: 767px ) {
        width: 150px;
      }
    }

    .order-line__remove {
      width: 60px;
      padding: 5px 10px;
    }
  }

  /deep/ .order-line--carriage {
    .order-line__data {
      padding: 0;
      padding-left: 10px;
      padding-top: 60px;
    }
  }

  /deep/ .delete-order-line {
    padding: 5px 10px;
  }

  /deep/.simple-basket__error {
    margin-top: 20px;
  }

  /deep/ .simple-basket__summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  /deep/ .order-totals,
  /deep/ .simple-basket__pricing {
    margin-bottom: 0 !important;
    display: flex;
    flex-direction: column;
  }

  /deep/ .order-totals__content {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  /deep/ .order-totals__table {
    font-weight: 600;
    margin-bottom: 10px;
    width: 100%;
    th {
      font-size: 16px;
      padding: 0 20px 0 0 !important;
    }

    td {
      padding: 0 !important;
      text-align: right;
    }

    tr {
      border-bottom: 0;
    }

  }

  /deep/ .simple-basket__empty {
    text-align: center;
    color: @color-accent-1;
    display: block;
    font-weight: 600;
    padding: 30px 15px;
  }

  /deep/ .apply-voucher {
    margin-top: 5px;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
  }

  /deep/ .apply-voucher__current-voucher {
    margin-bottom: 5px;
  }

  /deep/ .apply-voucher__input-container {
    display: flex;
  }

  /deep/ .apply-voucher__button {
    font-size: 14px;
    font-weight: 600;
    border-radius: 0;
    background: @color-accent-1;
    border: 0;
    color: #fff;
    padding: 5px 20px;
    text-decoration: none;

    &:hover {
      background: @color-accent-1-dark;
    }
  }

  .product-display-name {
    color: #000;
  }

  .product-display-icon {
    font-size: 10px;
    color: #555;
    display: inline-block;
    margin-left: 3px;
  }

  .edit-booking-btn {
    padding: 0;
    border: 0;

    &.invalid {
      color: red;
    }
  }

  .order-line-notification__error {
    color: red;
  }

  .booking-btn-invalid {
    color: red;
    display: inline-block;
    margin-left: 5px;
  }

  .loading-container {
    margin: 40px 0;
  }
}
</style>
